import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, CheckCircle, AlertCircle, Lock, HelpCircle, Download, Database } from 'lucide-react';
import api from '../../services/api';

const AdminConfig = () => {
    const [activeTab, setActiveTab] = useState('general');
    const [config, setConfig] = useState({
        orgName: '',
        orgLogoUrl: '',
        authMode: 'WHITELIST',
        allowedDomains: '',
        samlEntryPoint: '',
        samlIssuer: '',
        samlCert: '',
        baseCurrency: 'INR',
        smtpHost: '',
        smtpPort: '587',
        smtpUser: '',
        smtpPassword: '',
        smtpFrom: '',
    });

    const [showSamlHelp, setShowSamlHelp] = useState(false);
    const [isConfigured, setIsConfigured] = useState({ saml: false });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        try {
            const response = await api.get('/admin/config');
            setConfig(prev => ({
                ...prev,
                orgName: response.data.orgName || '',
                orgLogoUrl: response.data.orgLogoUrl || '',
                authMode: response.data.authMode || 'WHITELIST',
                allowedDomains: response.data.allowedDomains || '',
                samlEntryPoint: response.data.samlEntryPoint || '',
                samlIssuer: response.data.samlIssuer || '',
                samlCert: '', // Don't show secrets
                baseCurrency: response.data.baseCurrency || 'INR',
                smtpHost: response.data.smtpHost || '',
                smtpPort: response.data.smtpPort || '587',
                smtpUser: response.data.smtpUser || '',
                smtpFrom: response.data.smtpFrom || '',
                smtpPassword: '', // Masked
            }));
            setIsConfigured({ saml: response.data.samlAuthConfigured });
        } catch (error) {
            console.error('Failed to fetch config', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage(null);
        try {
            await api.post('/admin/config', config);
            setMessage({ type: 'success', text: 'Configuration saved successfully!' });
            fetchConfig();
        } catch (error) {
            console.error('Failed to save config', error);
            setMessage({ type: 'error', text: 'Failed to save configuration.' });
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (field, value) => {
        setConfig(prev => ({ ...prev, [field]: value }));
    };

    const handleMetadataUpload = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const parser = new DOMParser();
                const xml = parser.parseFromString(e.target.result, 'text/xml');

                // Helper: find elements by local name regardless of namespace prefix
                const findByLocalName = (localName) => Array.from(xml.getElementsByTagName('*'))
                    .filter(el => el.localName === localName);

                // Extract SSO URL — prefer HTTP-Redirect binding
                const ssoEls = findByLocalName('SingleSignOnService');
                let ssoUrl = '';
                for (let el of ssoEls) {
                    if ((el.getAttribute('Binding') || '').includes('HTTP-Redirect')) {
                        ssoUrl = el.getAttribute('Location') || '';
                        break;
                    }
                }
                if (!ssoUrl && ssoEls[0]) ssoUrl = ssoEls[0].getAttribute('Location') || '';

                // Extract Certificate
                const certEls = findByLocalName('X509Certificate');
                const cert = certEls[0] ? certEls[0].textContent.replace(/\s+/g, '') : '';

                console.log('[SAML Metadata] SSO URL:', ssoUrl);
                console.log('[SAML Metadata] Cert length:', cert.length);

                if (!ssoUrl && !cert) {
                    setMessage({ type: 'error', text: '⚠️ Could not find SSO URL or Certificate in the XML. Make sure you uploaded the correct IdP metadata file.' });
                    return;
                }

                setConfig(prev => ({
                    ...prev,
                    samlEntryPoint: ssoUrl || prev.samlEntryPoint,
                    samlCert: cert || prev.samlCert,
                }));
                setMessage({ type: 'success', text: `✅ Metadata parsed! SSO URL and Certificate filled automatically. Review the fields below and click Save.` });
            } catch (err) {
                console.error('Metadata parse error:', err);
                setMessage({ type: 'error', text: 'Failed to parse metadata XML: ' + err.message });
            }
        };
        reader.readAsText(file);
    };


    if (loading) return <div className="p-6">Loading configuration...</div>;

    const TabButton = ({ id, label, icon: Icon }) => (
        <button
            onClick={() => setActiveTab(id)}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '1rem 1.5rem',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                borderBottom: activeTab === id ? '2px solid var(--primary)' : 'none',
                color: activeTab === id ? 'var(--primary)' : 'var(--text-muted)',
                fontWeight: activeTab === id ? 600 : 400,
                transition: 'all 0.2s'
            }}
        >
            {Icon && <Icon size={18} />}
            {label}
        </button>
    );

    return (
        <div className="container section">
            <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <button onClick={() => navigate('/dashboard')} className="btn btn-secondary" style={{ padding: '0.5rem' }}>
                            <ArrowLeft size={20} />
                        </button>
                        <h1 className="gradient-text" style={{ fontSize: '2rem', margin: 0 }}>Organization Setup</h1>
                    </div>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <button onClick={() => navigate('/admin/logs')} className="btn btn-secondary">System Logs</button>
                        <button onClick={handleSave} disabled={saving} className="btn btn-primary">
                            <Save size={20} style={{ marginRight: '0.5rem' }} />
                            {saving ? 'Saving...' : 'Save All Changes'}
                        </button>
                    </div>
                </div>

                {message && (
                    <div className={`p-4 rounded-lg mb-6 flex items-center ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {message.type === 'success' ? <CheckCircle size={20} className="mr-2" /> : <AlertCircle size={20} className="mr-2" />}
                        {message.text}
                    </div>
                )}

                <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: '#f9fafb' }}>
                        <TabButton id="general" label="General" icon={Lock} />
                        <TabButton id="saml" label="SAML SSO" />
                        <TabButton id="email" label="Email (SMTP)" />
                    </div>

                    <div style={{ padding: '2rem' }}>
                        {activeTab === 'general' && (
                            <div style={{ display: 'grid', gap: '1.5rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Organization Name</label>
                                    <input
                                        type="text"
                                        value={config.orgName}
                                        onChange={(e) => handleChange('orgName', e.target.value)}
                                        className="form-input"
                                        placeholder="e.g. My Company Inc."
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Organization Logo URL</label>
                                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                        <input
                                            type="text"
                                            value={config.orgLogoUrl}
                                            onChange={(e) => handleChange('orgLogoUrl', e.target.value)}
                                            className="form-input"
                                            placeholder="https://example.com/logo.png"
                                            style={{ flex: 1 }}
                                        />
                                        {config.orgLogoUrl && (
                                            <img src={config.orgLogoUrl} alt="Preview" style={{ height: '40px', objectFit: 'contain', border: '1px solid var(--border)', borderRadius: '4px' }} />
                                        )}
                                    </div>
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>Visible on login page and navigation.</p>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Registration Policy</label>
                                    <select
                                        value={config.authMode}
                                        onChange={(e) => handleChange('authMode', e.target.value)}
                                        className="form-input"
                                    >
                                        <option value="WHITELIST">Controlled — Only allow pre-added users (Recommended)</option>
                                        <option value="AUTO_CREATE">Self-service — Auto-create any SSO user</option>
                                    </select>
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                                        {config.authMode === 'WHITELIST'
                                            ? '✅ Only users added by HR/Admin can log in. SSO will reject unknown accounts.'
                                            : '⚠️ Any user who passes SSO will be auto-created. Use with caution.'}
                                    </p>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Allowed Email Domains <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
                                    <input
                                        type="text"
                                        value={config.allowedDomains}
                                        onChange={(e) => handleChange('allowedDomains', e.target.value)}
                                        className="form-input"
                                        placeholder="e.g. pathtoip.com, mycompany.org"
                                    />
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>Comma-separated. Blocks logins from outside these domains. Leave blank to allow any domain.</p>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Base Currency (Global)</label>
                                    <select
                                        value={config.baseCurrency}
                                        onChange={(e) => handleChange('baseCurrency', e.target.value)}
                                        className="form-input"
                                    >
                                        <option value="INR">INR (₹)</option>
                                        <option value="USD">USD ($)</option>
                                        <option value="EUR">EUR (€)</option>
                                        <option value="GBP">GBP (£)</option>
                                        <option value="JPY">JPY (¥)</option>
                                        <option value="AUD">AUD (A$)</option>
                                        <option value="CAD">CAD (C$)</option>
                                    </select>
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                                        Used for Payroll and default conversion in Expense Reports.
                                    </p>
                                </div>

                                <div style={{ marginTop: '1rem', padding: '1.5rem', background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                                    <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <Database size={20} className="text-gray-600" />
                                        Backup & Maintenance
                                    </h3>
                                    <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
                                        Download a complete dump of the database in JSON format. This include users, expenses, leaves, and system configurations. Use this for quick off-site backups.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => window.open(`${api.defaults.baseURL}/admin/db-dump`, '_blank')}
                                        className="btn btn-secondary"
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                                    >
                                        <Download size={18} />
                                        Download Database Backup (JSON)
                                    </button>
                                </div>
                            </div>
                        )}

                        {activeTab === 'saml' && (
                            <div style={{ display: 'grid', gap: '1.5rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h3 style={{ margin: 0 }}>SAML SSO (Google Workspace, Okta, etc.)</h3>
                                    <button
                                        onClick={() => setShowSamlHelp(!showSamlHelp)}
                                        className="btn btn-secondary"
                                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}
                                    >
                                        <HelpCircle size={16} />
                                        {showSamlHelp ? 'Hide Setup Guide' : 'Show Setup Guide'}
                                    </button>
                                </div>

                                {/* Metadata XML Upload */}
                                <div style={{
                                    border: '2px dashed #c7d2fe',
                                    borderRadius: 'var(--radius)',
                                    padding: '1.5rem',
                                    background: '#f5f3ff',
                                    textAlign: 'center'
                                }}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => { e.preventDefault(); handleMetadataUpload(e.dataTransfer.files[0]); }}
                                >
                                    <Lock size={24} style={{ color: '#6366f1', marginBottom: '0.5rem' }} />
                                    <p style={{ fontWeight: 600, margin: '0 0 0.25rem' }}>Upload IdP Metadata XML <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(fastest setup)</span></p>
                                    <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0 0 0.75rem' }}>Download from Google Admin Console → your SAML app → Download metadata. <br />Drag & drop here or click to browse.</p>
                                    <label style={{ cursor: 'pointer' }}>
                                        <input
                                            type="file"
                                            accept=".xml,text/xml,application/xml"
                                            style={{ display: 'none' }}
                                            onChange={(e) => handleMetadataUpload(e.target.files[0])}
                                        />
                                        <span className="btn btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                                            📂 Browse for XML file
                                        </span>
                                    </label>
                                </div>

                                {showSamlHelp && (
                                    <div style={{
                                        background: '#f8fafc',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: 'var(--radius)',
                                        padding: '1.5rem',
                                        fontSize: '0.9rem',
                                        lineHeight: 1.6,
                                        color: '#334155'
                                    }}>
                                        <h4 style={{ marginTop: 0, color: '#0f172a' }}>Google Workspace Setup</h4>
                                        <ol style={{ paddingLeft: '1.2rem', marginBottom: '1.5rem' }}>
                                            <li>Go to <a href="https://admin.google.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>Google Admin Console</a> &gt; <strong>Apps</strong> &gt; <strong>Web and mobile apps</strong>.</li>
                                            <li><strong>Add App</strong> &gt; <strong>Add custom SAML app</strong>.</li>
                                            <li><strong>Copy</strong> the <strong>SSO URL</strong>, <strong>Entity ID</strong>, and <strong>Download Certificate</strong>. Paste them below.</li>
                                            <li>Continue and name the app "PathToIP".</li>
                                            <li><strong>Service Provider Details:</strong>
                                                <ul style={{ listStyle: 'disc', marginLeft: '1rem', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                                                    <li><strong>ACS URL:</strong> <code>{window.location.origin}/api/auth/saml/callback</code></li>
                                                    <li><strong>Entity ID:</strong> <code>{window.location.origin}</code></li>
                                                    <li><strong>Name ID format:</strong> EMAIL</li>
                                                </ul>
                                            </li>
                                            <li>Finish and ensure user access is turned <strong>ON</strong>.</li>
                                        </ol>
                                        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                                            Map <strong>Primary Email</strong> → <code>email</code>, <strong>First Name</strong> → <code>firstName</code>, <strong>Last Name</strong> → <code>lastName</code>.
                                        </p>
                                    </div>
                                )}

                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: '#f0f9ff', padding: '1rem', borderRadius: 'var(--radius)', border: '1px solid #bae6fd' }}>
                                    <AlertCircle className="text-blue-600" size={24} />
                                    <div>
                                        <p style={{ fontWeight: 600, margin: 0 }}>Service Provider Info (For your IdP)</p>
                                        <p style={{ fontSize: '0.85rem', margin: 0 }}><strong>ACS URL:</strong> {window.location.origin}/api/auth/saml/callback</p>
                                        <p style={{ fontSize: '0.85rem', margin: 0 }}><strong>Entity ID:</strong> {window.location.origin}</p>
                                    </div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>SSO URL (Entry Point) — <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>Google's login URL</span></label>
                                    <textarea
                                        rows={3}
                                        value={config.samlEntryPoint}
                                        onChange={(e) => handleChange('samlEntryPoint', e.target.value)}
                                        className="form-input"
                                        style={{ fontFamily: 'monospace', fontSize: '0.85rem', resize: 'vertical' }}
                                        placeholder="e.g. https://accounts.google.com/o/saml2/idp?idpid=..."
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Entity ID (Issuer) — <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>Must exactly match what you entered in Google Admin Console → SP Details</span></label>
                                    <textarea
                                        rows={2}
                                        value={config.samlIssuer}
                                        onChange={(e) => handleChange('samlIssuer', e.target.value)}
                                        className="form-input"
                                        style={{ fontFamily: 'monospace', fontSize: '0.85rem', resize: 'vertical' }}
                                        placeholder={`e.g. https://pathtoiptoolslab-git-827131241084.asia-south1.run.app/saml`}
                                    />
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>⚠️ This must be identical in both Google and here — it's how Google recognises your app.</p>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Certificate (X.509)</label>
                                    <textarea
                                        rows={10}
                                        value={config.samlCert}
                                        onChange={(e) => handleChange('samlCert', e.target.value)}
                                        className="form-input"
                                        style={{ fontFamily: 'monospace', fontSize: '0.8rem', resize: 'vertical' }}
                                        placeholder={isConfigured.saml ? "•••••••• (Paste new certificate to update)" : "-----BEGIN CERTIFICATE-----\nMIID...\n-----END CERTIFICATE-----"}
                                    />
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>Paste the entire contents of the certificate file downloaded from Google Admin Console.</p>
                                </div>

                                {isConfigured.saml && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#16a34a', fontWeight: 600 }}>
                                        <CheckCircle size={18} />
                                        SAML SSO is configured. Users will see a "Login with SSO" button on the login page.
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'email' && (
                            <div style={{ display: 'grid', gap: '1.5rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: '#fefce8', padding: '1rem', borderRadius: 'var(--radius)', border: '1px solid #fef08a', marginBottom: '1rem' }}>
                                    <AlertCircle className="text-yellow-600" size={24} />
                                    <div>
                                        <p style={{ fontWeight: 600, margin: 0 }}>Google Workspace Tip</p>
                                        <p style={{ fontSize: '0.85rem', margin: 0 }}>Use <strong>smtp.gmail.com</strong> (Port 587) and a 16-character <strong>App Password</strong> if you have 2FA enabled.</p>
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>SMTP Host</label>
                                        <input
                                            type="text"
                                            value={config.smtpHost}
                                            onChange={(e) => handleChange('smtpHost', e.target.value)}
                                            className="form-input"
                                            placeholder="e.g. smtp.gmail.com"
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>SMTP Port</label>
                                        <input
                                            type="text"
                                            value={config.smtpPort}
                                            onChange={(e) => handleChange('smtpPort', e.target.value)}
                                            className="form-input"
                                            placeholder="587"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>SMTP User (Username)</label>
                                    <input
                                        type="email"
                                        value={config.smtpUser}
                                        onChange={(e) => handleChange('smtpUser', e.target.value)}
                                        className="form-input"
                                        placeholder="your-email@gmail.com"
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>SMTP Password / App Password</label>
                                    <input
                                        type="password"
                                        value={config.smtpPassword}
                                        onChange={(e) => handleChange('smtpPassword', e.target.value)}
                                        className="form-input"
                                        placeholder="••••••••••••••••"
                                    />
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>Leave blank to keep existing password.</p>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Sender Email (From)</label>
                                    <input
                                        type="email"
                                        value={config.smtpFrom}
                                        onChange={(e) => handleChange('smtpFrom', e.target.value)}
                                        className="form-input"
                                        placeholder="notifications@yourdomain.com"
                                    />
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>The email address shown to recipients.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminConfig;
